import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import translations from './translations';
import ProxyForm from '../ProxyForm';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import { proxy as proxyApi } from '@afosto/quicq/api/Proxy';
import { useSnackbar } from 'notistack';

const formatRoutes = routes =>
  routes.reduce(
    (acc, route) => [
      ...acc,
      {
        path: route?.path,
        origin: {
          is_bucket: route?.isBucket,
          uri: route?.uri,
        },
        settings: {
          cache: {
            is_as_origin: route?.isAsOrigin,
            is_public: route?.isPublic,
            age: route?.age,
          },
          auth: {
            is_required: route?.isRequired,
            scope: route?.scope,
          },
        },
      },
    ],
    [],
  );

const ProxyFormDrawer = ({ open, onClose, onSuccess, proxy }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const handleCreateProxy = async proxyData => {
    try {
      const {
        key,
        routes,
        authSignatureSecret,
        authSignatureAuthIsRequired,
        authSignatureAuthScope,
        authSignatureCacheIsAsOrigin,
        authSignatureCacheIsPublic,
        authSignatureCacheAge,
        oauthIssuer,
        oauthClientId,
        oauthAuthorizeUrl,
        oauthReturnUrlKey,
      } = proxyData ?? {};

      const body = {
        key,
        routes: formatRoutes(routes),
        auth: {
          signature: {
            secret: authSignatureSecret,
            auth: {
              is_required: authSignatureAuthIsRequired,
              scope: authSignatureAuthScope,
            },
            cache: {
              age: authSignatureCacheAge,
              is_as_origin: authSignatureCacheIsAsOrigin,
              is_public: authSignatureCacheIsPublic,
            },
          },
        },
        oauth: {
          issuer: oauthIssuer,
          client_id: oauthClientId,
          authorize_url: oauthAuthorizeUrl,
          return_url_key: oauthReturnUrlKey,
        },
      };

      await proxyApi.createQuicqProxy(body);

      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.proxyCreated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.proxyNotCreated), {
        variant: 'error',
      });
    }
  };

  const handleUpdateProxy = async ({ key, ...proxyData }) => {
    try {
      const {
        routes,
        authSignatureSecret,
        authSignatureAuthIsRequired,
        authSignatureAuthScope,
        authSignatureCacheIsAsOrigin,
        authSignatureCacheIsPublic,
        authSignatureCacheAge,
        oauthIssuer,
        oauthClientId,
        oauthAuthorizeUrl,
        oauthReturnUrlKey,
      } = proxyData ?? {};
      const body = {
        routes: formatRoutes(routes),
        auth: {
          signature: {
            secret: authSignatureSecret,
            auth: {
              is_required: authSignatureAuthIsRequired,
              scope: authSignatureAuthScope,
            },
            cache: {
              age: authSignatureCacheAge,
              is_as_origin: authSignatureCacheIsAsOrigin,
              is_public: authSignatureCacheIsPublic,
            },
          },
        },
        oauth: {
          issuer: oauthIssuer,
          client_id: oauthClientId,
          authorize_url: oauthAuthorizeUrl,
          return_url_key: oauthReturnUrlKey,
        },
      };

      await proxyApi.updateQuicqProxy(key, body);

      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }

      enqueueSnackbar(intl.formatMessage(translations.feedback.proxyUpdated), {
        variant: 'success',
      });
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.proxyNotUpdated), {
        variant: 'error',
      });
    }
  };

  return (
    <FormDrawer
      isOpen={open}
      title={
        proxy
          ? intl.formatMessage(translations.proxy.edit.title)
          : intl.formatMessage(translations.proxy.create.title)
      }
      drawerWidth="400px"
      onClose={onClose}
    >
      <ProxyForm
        onSubmit={proxy ? handleUpdateProxy : handleCreateProxy}
        onCancel={onClose}
        proxy={proxy}
      />
    </FormDrawer>
  );
};

ProxyFormDrawer.propTypes = {
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  open: PropTypes.bool,
  proxy: PropTypes.object,
};

ProxyFormDrawer.defaultProps = {
  onClose: undefined,
  onSuccess: undefined,
  open: false,
  proxy: undefined,
};

export default ProxyFormDrawer;
